// Live delivery tracking — the pure pieces shared by the driver's phone (which
// publishes positions), the owner's screens and the customer's public page
// (which consume them). No React, no Leaflet, no network here: everything is
// unit-testable and the transport lives in driverGps.js / useLiveDelivery.js.
//
// Transport: the driver upserts driver_locations (persistent "last known
// position", read by the backend for /api/track) AND broadcasts the same fix on
// a Supabase Realtime channel named after the order's tracking_token. Viewers
// subscribe to that channel for sub-second updates and keep polling the
// backend as a fallback, so a viewer never depends on the websocket alone.

// One channel per order, keyed by the unguessable tracking_token (a UUID) so
// the public tracking page can listen without an account.
export const TRACK_CHANNEL_PREFIX = 'delivery:';
export function trackingChannel(token) {
  return `${TRACK_CHANNEL_PREFIX}${token}`;
}

// Broadcast event names on the channel.
export const EV_POSITION = 'pos';
export const EV_STATUS = 'status';

// A driver position is "live" while it is younger than this; beyond it the
// UI shows the last known point greyed out with a "no recent signal" note.
export const FRESH_MS = 90 * 1000;

// Urban average used for the "arrives in ~N min" estimate. Deliberately
// conservative: an early estimate annoys more than a late one.
export const ETA_SPEED_KMH = 22;

// Publishing cadence: never faster than MIN_INTERVAL, always at least every
// MAX_INTERVAL (heartbeat, keeps the viewer's freshness dot green while the
// van is stopped at a light), and in between only when the phone moved.
export const SEND_MIN_INTERVAL_MS = 5000;
export const SEND_MAX_INTERVAL_MS = 30000;
export const SEND_MIN_MOVE_M = 8;

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

// Validate a raw fix (GeolocationCoordinates, a broadcast payload or a
// driver_locations row) into the one shape the rest of the code uses.
// Returns null when there is no usable lat/lng.
export function normalizePosition(p) {
  if (!p) return null;
  const lat = Number(p.lat ?? p.latitude);
  const lng = Number(p.lng ?? p.longitude);
  if (!isNum(lat) || !isNum(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  const heading = Number(p.heading);
  const accuracy = Number(p.accuracy);
  const speed = Number(p.speed);
  const at = p.at || p.updated_at || null;
  return {
    lat, lng,
    heading: isNum(heading) && heading >= 0 ? heading : null,
    accuracy: isNum(accuracy) && accuracy >= 0 ? Math.round(accuracy) : null,
    speed: isNum(speed) && speed >= 0 ? speed : null,
    at: at ? new Date(at).toISOString() : new Date().toISOString(),
  };
}

// Great-circle distance in km between two {lat,lng} points.
export function haversineKm(a, b) {
  if (!a || !b) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Whole minutes for the remaining distance, never below 1.
export function etaMinutes(km, speedKmh = ETA_SPEED_KMH) {
  if (!isNum(km) || km < 0) return null;
  return Math.max(1, Math.ceil((km / speedKmh) * 60));
}

// Is a fix recent enough to be shown as live?
export function isFreshLocation(at, now = Date.now(), maxAgeMs = FRESH_MS) {
  if (!at) return false;
  const t = new Date(at).getTime();
  if (!Number.isFinite(t)) return false;
  return now - t <= maxAgeMs;
}

// Age of a fix in whole seconds (0 when the clock skews negative).
export function ageSeconds(at, now = Date.now()) {
  if (!at) return null;
  const t = new Date(at).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((now - t) / 1000));
}

// i18n parts for a "hace 12 s" / "hace 3 min" label; the caller does t(key, { n }).
export function ageLabel(seconds) {
  if (seconds == null) return null;
  return seconds < 60
    ? { key: 'deliv.agoSeconds', n: seconds }
    : { key: 'deliv.agoMinutes', n: Math.max(1, Math.round(seconds / 60)) };
}

// Throttle for the driver's publisher: send when the last send is older than
// the heartbeat, or when the minimum interval passed AND the phone moved.
export function shouldSendPosition(prev, next, nowMs, opts = {}) {
  const minInterval = opts.minIntervalMs ?? SEND_MIN_INTERVAL_MS;
  const maxInterval = opts.maxIntervalMs ?? SEND_MAX_INTERVAL_MS;
  const minMove = opts.minMoveM ?? SEND_MIN_MOVE_M;
  if (!next) return false;
  if (!prev || !prev.sentAt) return true;
  const elapsed = nowMs - prev.sentAt;
  if (elapsed >= maxInterval) return true;
  if (elapsed < minInterval) return false;
  const movedM = (haversineKm(prev, next) || 0) * 1000;
  return movedM >= minMove;
}

// Public link the customer opens (no login). Built from the current origin so
// it is right on app.zcom.es, a preview host and localhost alike.
export function trackingUrl(token, origin) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/track/${token}`;
}

// WhatsApp deep link. wa.me wants digits only with the country code and no
// leading +/00; a local number (Spain 9 digits, Pakistan 0XXXXXXXXXX) gets the
// account's own prefix so the message reaches the customer, not a wrong number.
const COUNTRY_PREFIX = { ES: '34', PK: '92' };
export function whatsappNumber(phone, countryCode = 'ES') {
  let d = String(phone || '').replace(/[^\d+]/g, '');
  if (!d) return '';
  if (d.startsWith('+')) d = d.slice(1);
  else if (d.startsWith('00')) d = d.slice(2);
  else {
    const prefix = COUNTRY_PREFIX[(countryCode || 'ES').toUpperCase()] || COUNTRY_PREFIX.ES;
    if (d.startsWith('0')) d = d.slice(1);
    if (!d.startsWith(prefix)) d = prefix + d;
  }
  return d.replace(/\D/g, '');
}
export function whatsappShareUrl(phone, text, countryCode) {
  const n = whatsappNumber(phone, countryCode);
  const q = `text=${encodeURIComponent(text || '')}`;
  return n ? `https://wa.me/${n}?${q}` : `https://wa.me/?${q}`;
}

// Google Maps turn-by-turn for the driver (opens the phone's maps app).
export function directionsUrl(address, dest) {
  if (dest && isNum(dest.lat) && isNum(dest.lng)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}&travelmode=driving`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || '')}&travelmode=driving`;
}

// ---------------------------------------------------------------------------
// Driver GPS status store — a tiny module-level store so any screen (the
// delivery page, the list, a future badge in the header) can show whether the
// phone is currently sharing its position, without prop drilling through
// Layout. driverGps.js is the only writer.
// ---------------------------------------------------------------------------
let gpsStatus = { sharing: false, error: null, active: 0, lastSentAt: null };
const gpsListeners = new Set();
export function getGpsStatus() { return gpsStatus; }
export function setGpsStatus(patch) {
  const next = { ...gpsStatus, ...patch };
  const changed = Object.keys(next).some((k) => next[k] !== gpsStatus[k]);
  if (!changed) return;
  gpsStatus = next;
  gpsListeners.forEach((fn) => { try { fn(gpsStatus); } catch { /* listener error must not break the publisher */ } });
}
export function subscribeGpsStatus(fn) {
  gpsListeners.add(fn);
  return () => gpsListeners.delete(fn);
}
